


























import {
    defineComponent,
    computed,
    ref,
    watch,
    nextTick,
    onMounted,
    onUnmounted
} from "@vue/composition-api";
import { Chart, ChartItem, registerables, TimeUnit } from "chart.js";
import "chartjs-adapter-date-fns";
import useBatteryStatistic, { BatteryDataPoint } from "@/store/useBatteryStatistic";

Chart.register(...registerables);

export default defineComponent({
    props: {
        imgSrc: { type: String },
        value: { type: Boolean, default: false },
        deviceType: { type: Number, default: 0 },
        id: { type: String, required: true },
        subtitle: { type: String, default: "" }
    },
    setup(props, context) {
        const chartCanvas = ref<HTMLCanvasElement>();
        const toggle_chart = ref<number>(0);
        const dialog = computed(() => props.value);

        const { getBatteryData } = useBatteryStatistic();
        let chart: Chart | null = null;

        const getChartConfig = (value: number): { scale: TimeUnit; title: string } => {
            switch (value) {
                case 0: return { scale: "day", title: "Daily charge level (7 Days)" };
                case 1: return { scale: "day", title: "Daily charge level (30 Days)" };
                case 2: return { scale: "week", title: "Weekly charge level (90 Days)" };
                case 3: return { scale: "month", title: "Monthly charge level (365 Days)" };
                default: return { scale: "day", title: "Charge level" };
            }
        };

        const setupChart = async () => {
            const ctx = chartCanvas.value as ChartItem;
            if (!ctx) return;

            const interval = [7, 30, 90, 365][toggle_chart.value] || 7;
            const { scale, title } = getChartConfig(toggle_chart.value);

            const data = await getBatteryData(props.id, interval);

            chart = new Chart(ctx, {
                type: "line",
                data: {
                    datasets: [
                        {
                            label: "Voltage",
                            data: data,
                            fill: true,
                            borderColor: "rgb(75, 192, 192)",
                            tension: 0.1
                        }
                    ]
                } as any,
                options: {
                    plugins: {
                        title: {
                            display: true,
                            text: title,
                            align: "center",
                            position: "top",
                            padding: { top: 10, bottom: 10 }
                        },
                        subtitle: {
                            display: true,
                            text: props.subtitle,
                            align: "center",
                            padding: { bottom: 10 }
                        },
                        legend: {
                            display: false
                        }
                    },
                    elements: {
                        point: {
                            radius: 9,
                            hitRadius: 3,
                            hoverRadius: 12
                        }
                    },
                    parsing: {
                        xAxisKey: "date",
                        yAxisKey: "voltage"
                    },
                    scales: {
                        x: {
                            title: { display: false },
                            type: "time",
                            time: {
                                unit: scale,
                                displayFormats: {
                                    day: "dd-MMM",
                                    week: "dd-MMM",
                                    month: "MMM yyyy",
                                    quarter: "MMM yyyy",
                                    year: "yyyy"
                                }
                            }
                        },
                        y: {
                            title: {
                                display: true,
                                text: "Voltage"
                            }
                        }
                    }
                }
            });

            // Optional: Set responsive width
            (chart.canvas as HTMLCanvasElement).style.width = "auto";
        };

        const rebuildChart = async () => {
            if (chart) {
                chart.destroy();
                chart = null;
            }
            await nextTick();
            setupChart();
        };

        const close = () => {
            if (chart) {
                chart.destroy();
                chart = null;
            }
            context.emit("input", false);
        };

        onMounted(() => {
            if (props.value) setupChart();
        });

        onUnmounted(() => {
            if (chart) {
                chart.destroy();
                chart = null;
            }
        });

        watch(() => props.value, (val) => {
            if (val) rebuildChart();
        });

        watch(() => toggle_chart.value, () => {
            rebuildChart();
        });

        return {
            dialog,
            close,
            chartCanvas,
            toggle_chart
        };
    }
});
